import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Search, Target, User, Users, X } from 'lucide-react';
import { classNames } from '../lib/utils';
import { buildPersonOkrIndex, getPersonInitials } from '../lib/personOkrIndex';
import KPIProgressBar from './ui/KPIProgressBar';

function statusBadge(progress) {
  if (progress >= 1.0) return { label: 'Ahead', badge: 'bg-emerald-100 text-emerald-700' };
  if (progress >= 0.7) return { label: 'On Track', badge: 'bg-green-100 text-green-700' };
  if (progress >= 0.3) return { label: 'At Risk', badge: 'bg-amber-100 text-amber-700' };
  return { label: 'Behind', badge: 'bg-red-100 text-red-700' };
}

function Pct({ value }) {
  return <span className="text-xs font-bold text-gray-700 tabular-nums w-9 text-right">{Math.round((value || 0) * 100)}%</span>;
}

/**
 * Number field that keeps its own draft while typing and commits on blur /
 * Enter, so a Firestore round-trip never fights the caret.
 */
function QuarterField({ label, value, editable, accent, onCommit }) {
  const [draft, setDraft] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => { if (!isFocused) setDraft(value ?? ''); }, [value, isFocused]);

  const commit = () => {
    setIsFocused(false);
    const next = draft === '' ? null : parseFloat(draft);
    const normalized = Number.isNaN(next) ? null : next;
    if (normalized !== (value ?? null)) onCommit(normalized);
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] text-slate-500 uppercase font-semibold">{label}</span>
      {editable ? (
        <input
          type="number"
          value={draft ?? ''}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { setDraft(value ?? ''); setIsFocused(false); e.currentTarget.blur(); }
          }}
          onClick={(e) => e.stopPropagation()}
          className={classNames(
            'w-20 px-1.5 py-0.5 border rounded text-[11px] font-semibold text-right focus:ring-1 focus:ring-blue-500 outline-none',
            accent ? 'border-blue-200 bg-white' : 'border-slate-200 bg-white'
          )}
          placeholder="—"
        />
      ) : (
        <span className="text-[11px] font-semibold text-slate-700">{value ?? '—'}</span>
      )}
    </div>
  );
}

/**
 * Owner picker for a single IKR, admin-only.
 *
 * Type-ahead mechanics mirror the owner picker in OwnerIKRThreadFY2627: select on
 * onMouseDown so it fires before the input's blur, and delay the blur close so the
 * click lands. Picking a person opens a confirm step rather than writing straight
 * away, because the move also carries the IKR into the new owner's evaluation list.
 */
function ReassignControl({ ikr, users, onReassign, onNotice }) {
  const [isPicking, setIsPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(null);   // { id, label } awaiting confirmation
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');

  const owner = users.find(u => (u.uid || u.id) === ikr.ownerId) || null;
  const ownerLabel = ikr.ownerId ? (owner?.name || owner?.email || 'Unknown user') : 'Unassigned';

  // The current owner is excluded, so "you picked the existing owner" is unreachable.
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter(u => (u.uid || u.id) && (u.uid || u.id) !== ikr.ownerId)
      .filter(u => !q
        || (u.name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [users, search, ikr.ownerId]);

  const reset = () => { setIsPicking(false); setPending(null); setSearch(''); setError(''); };

  const commit = async () => {
    if (!pending) return;
    setIsBusy(true);
    setError('');
    const result = await onReassign(ikr.path, pending.id);
    setIsBusy(false);
    if (result?.ok) {
      // The row unmounts on the next snapshot, so success has to surface above.
      if (onNotice) onNotice({ tone: 'success', text: `Moved to ${pending.label}.` });
      reset();
    } else {
      setError(result?.message || 'Reassign failed.');
    }
  };

  if (pending) {
    return (
      <div className="rounded border border-blue-200 bg-blue-50/70 p-2">
        <p className="text-[11px] text-slate-700 leading-snug">
          Move this IKR to <span className="font-bold">{pending.label}</span>?
          {' '}Existing evaluations stay with {ownerLabel}; the new owner starts fresh.
        </p>
        {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
        <div className="flex items-center gap-2 mt-2">
          <button type="button" onClick={commit} disabled={isBusy}
            className="text-[11px] font-semibold px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {isBusy ? 'Moving…' : 'Confirm'}
          </button>
          <button type="button" onClick={reset} disabled={isBusy}
            className="text-[11px] font-semibold px-2.5 py-1 rounded bg-white border border-slate-200 text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-[10px] text-slate-500 uppercase font-semibold">
        Owner <span className="text-slate-700 font-bold normal-case">{ownerLabel}</span>
      </span>

      {isPicking ? (
        <div className="w-full sm:w-64">
          <div className="flex items-center gap-1">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a person…"
              className="flex-1 px-2 py-1 border border-blue-200 rounded text-[11px] focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <button type="button" onClick={reset} title="Cancel"
              className="text-slate-400 hover:text-slate-600 p-0.5"><X size={12} /></button>
          </div>
          <ul className="mt-1 max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-sm">
            {ikr.ownerId && (
              <li onMouseDown={() => setPending({ id: null, label: 'Unassigned' })}
                className="px-2.5 py-1.5 text-[11px] text-slate-600 italic cursor-pointer hover:bg-slate-50 border-b border-slate-100">
                Unassign
              </li>
            )}
            {candidates.length === 0 && (
              <li className="px-2.5 py-2 text-[11px] text-slate-400">No people match.</li>
            )}
            {candidates.map(u => (
              <li key={u.uid || u.id}
                onMouseDown={() => setPending({ id: u.uid || u.id, label: u.name || u.email })}
                className="px-2.5 py-1.5 cursor-pointer hover:bg-blue-50 border-b border-slate-100 last:border-b-0">
                <span className="block text-[11px] font-semibold text-slate-800 truncate">{u.name || u.email}</span>
                {u.name && u.email && <span className="block text-[10px] text-slate-400 truncate">{u.email}</span>}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <button type="button" onClick={() => { setIsPicking(true); setError(''); }}
          className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 bg-white text-blue-700 hover:bg-blue-50">
          Reassign
        </button>
      )}
    </div>
  );
}

function IkrCard({ ikr, canEditTarget, canEditAllActuals, viewerUid, supportsQuarterTarget, onQuarterChange, canReassign, reassignUsers, onReassign, onReassignNotice }) {
  const [showQuarters, setShowQuarters] = useState(false);
  const status = statusBadge(ikr.progress);
  // Same shape as the main tracking UI / Firestore rules: admins edit anything,
  // everyone else may only touch actuals on IKRs they own.
  const canEditActual = canEditAllActuals || (!!viewerUid && ikr.ownerId === viewerUid);

  return (
    <div className="rounded-md border border-slate-200 bg-white mb-1.5 last:mb-0 overflow-hidden">
      {/* Whole bar toggles the quarter breakdown */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowQuarters(!showQuarters)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowQuarters(!showQuarters); } }}
        aria-expanded={showQuarters}
        className="w-full flex items-start gap-2 p-2.5 text-left cursor-pointer hover:bg-slate-50 transition-colors"
      >
        {showQuarters
          ? <ChevronDown size={13} className="mt-0.5 text-slate-400 flex-shrink-0" />
          : <ChevronRight size={13} className="mt-0.5 text-slate-400 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-700 leading-snug">{ikr.text || <span className="italic text-slate-400">No IKR text</span>}</p>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <span className="text-[10px] text-slate-500 uppercase font-semibold">
              Target <span className="text-slate-700 font-bold">{ikr.target ?? '—'}{ikr.unit ? ` ${ikr.unit}` : ''}</span>
            </span>
            <span className="text-[10px] text-slate-500 uppercase font-semibold capitalize">
              Method <span className="text-slate-700 font-bold">{ikr.calculationMethod}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status.badge}`}>{status.label}</span>
          <div className="w-20"><KPIProgressBar value={ikr.progress} /></div>
          <Pct value={ikr.progress} />
        </div>
      </div>

      {showQuarters && (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 px-2.5 pb-2.5 pt-2 border-t border-slate-100 bg-slate-50/60">
          {ikr.quarters.map((q, i) => (
            <div key={q.q} className="rounded border border-slate-200 bg-white p-2">
              <p className="text-[10px] font-bold text-slate-600 uppercase mb-1">{q.q}</p>
              {q.text && <p className="text-[11px] text-slate-600 mb-1.5">{q.text}</p>}
              <div className="space-y-1.5">
                {/* FY25-26 has no per-quarter target — the IKR-level target above governs. */}
                {supportsQuarterTarget && (
                  <QuarterField
                    label="Target"
                    value={q.target}
                    editable={canEditTarget}
                    onCommit={(v) => onQuarterChange(ikr.path, i, 'target', v)}
                  />
                )}
                <QuarterField
                  label="Actual"
                  value={q.actual}
                  editable={canEditActual}
                  accent
                  onCommit={(v) => onQuarterChange(ikr.path, i, 'actual', v)}
                />
              </div>
            </div>
          ))}
        </div>
        {/* Lives here rather than in the header above: that header is a role="button"
            toggle, so nesting a button and a text input inside it would be invalid
            and would need click-suppression on every interaction. */}
        {canReassign && (
          <div className="px-2.5 pb-2.5 border-t border-slate-100 bg-slate-50/60 pt-2">
            <ReassignControl
              ikr={ikr}
              users={reassignUsers || []}
              onReassign={onReassign}
              onNotice={onReassignNotice}
            />
          </div>
        )}
        </>
      )}
    </div>
  );
}

function TeamKRBlock({ teamKR, editProps }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const status = statusBadge(teamKR.progress);

  return (
    <div className="rounded-lg border border-gray-200 bg-white mb-2 last:mb-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-gray-50/70 transition-colors"
      >
        {isExpanded ? <ChevronDown size={14} className="mt-0.5 text-gray-400 flex-shrink-0" /> : <ChevronRight size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-0.5">Team Key Result</p>
          <p className="text-sm font-semibold text-gray-800 leading-snug">{teamKR.text || <span className="italic text-gray-400">Untitled team KR</span>}</p>
          {(teamKR.targetKPI || teamKR.unit) && (
            <span className="inline-flex mt-1 text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
              Target: <span className="font-medium text-gray-700 ml-1">{teamKR.targetKPI || '—'} {teamKR.unit || ''}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status.badge}`}>{status.label}</span>
          <div className="w-20 hidden sm:block"><KPIProgressBar value={teamKR.progress} /></div>
          <Pct value={teamKR.progress} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-2.5 pb-2.5 pt-2 border-t border-gray-100 bg-gray-50/40">
          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">
            Individual Key Result{teamKR.ikrs.length !== 1 ? 's' : ''} ({teamKR.ikrs.length})
          </p>
          {teamKR.ikrs.map(ikr => <IkrCard key={ikr.id} ikr={ikr} {...editProps} />)}
        </div>
      )}
    </div>
  );
}

function ObjectiveBlock({ objective, editProps }) {
  /** Collapsed on arrival — open just the objective you care about. */
  const [isExpanded, setIsExpanded] = useState(false);
  const status = statusBadge(objective.progress);

  return (
    <div className="rounded-xl border border-gray-200 bg-white mb-3 last:mb-0 overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-2 p-3 text-left hover:bg-gray-50/70 transition-colors"
      >
        {isExpanded ? <ChevronDown size={16} className="mt-1 text-gray-400 flex-shrink-0" /> : <ChevronRight size={16} className="mt-1 text-gray-400 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            {objective.code && (
              <span className="text-[11px] font-mono font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">{objective.code}</span>
            )}
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status.badge}`}>{status.label}</span>
            <span className="text-[11px] text-gray-400">{objective.ikrCount} IKR{objective.ikrCount !== 1 ? 's' : ''}</span>
          </div>
          <p className="text-sm font-bold text-gray-800 leading-snug">{objective.objective}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 mt-1">
          <div className="w-24 hidden sm:block"><KPIProgressBar value={objective.progress} /></div>
          <Pct value={objective.progress} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 bg-gray-50/30">
          {objective.krs.map(kr => (
            <div key={kr.id} className="mb-2 last:mb-0">
              {!kr.isFlat && (
                <div className="flex items-center gap-2 mb-1.5 pl-0.5">
                  {kr.code && (
                    <span className="text-[11px] font-mono font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded flex-shrink-0">{kr.code}</span>
                  )}
                  <p className="text-xs font-semibold text-gray-600 truncate">{kr.text}</p>
                </div>
              )}
              {kr.teamKRs.map(tkr => <TeamKRBlock key={tkr.id} teamKR={tkr} editProps={editProps} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Person search for OKR Tracking.
 *
 * Type a name, pick a person, and see everything they own laid out as
 * Objective → (Key Result) → Team Key Result → Individual Key Results.
 * Clicking an IKR bar drops down its four quarters, where Target / Actual are
 * editable subject to the same rules as the main tracking UI.
 */
export default function PersonOKRSearch({
  okrs,
  users,
  variant = 'FY2627',
  defaultPersonId = null,
  canEditTarget = false,
  canEditAllActuals = false,
  canReassign = false,
  viewerUid = null,
  onQuarterChange,
  onReassign,
  onSelectionChange,
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [selectedPersonId, setSelectedPersonId] = useState(defaultPersonId);
  /** Reassign confirmations surface here: the IKR row itself unmounts on success. */
  const [notice, setNotice] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const people = useMemo(() => buildPersonOkrIndex(okrs, users, variant), [okrs, users, variant]);

  /** Candidates for the owner picker — anyone with an id and something to show. */
  const reassignUsers = useMemo(() => (users || [])
    .filter(u => (u.uid || u.id) && (u.name || u.email))
    .slice()
    .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '')),
  [users]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      p.role.toLowerCase().includes(q)
    );
  }, [people, query]);

  const selectedPerson = useMemo(
    () => people.find(p => p.id === selectedPersonId) || null,
    [people, selectedPersonId]
  );

  useEffect(() => { setHighlightIndex(0); }, [query, isOpen]);

  useEffect(() => { setNotice(null); }, [selectedPersonId]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [notice]);

  // Let the host view collapse the full objective list while a person is picked.
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedPerson);
  }, [selectedPerson, onSelectionChange]);

  const editProps = useMemo(() => ({
    canEditTarget,
    canEditAllActuals,
    viewerUid,
    supportsQuarterTarget: variant === 'FY2627',
    canReassign: canReassign && !!onReassign,
    reassignUsers,
    onReassign: (path, newOwnerId) => (
      onReassign ? onReassign(path, newOwnerId) : Promise.resolve({ ok: false, message: 'Reassign unavailable.' })
    ),
    onReassignNotice: setNotice,
    onQuarterChange: (path, quarterIndex, field, value) => {
      if (onQuarterChange) onQuarterChange(path, quarterIndex, field, value);
    },
  }), [canEditTarget, canEditAllActuals, viewerUid, variant, onQuarterChange, canReassign, onReassign, reassignUsers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectPerson = (person) => {
    setSelectedPersonId(person.id);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const clearSelection = () => {
    setSelectedPersonId(null);
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightIndex(i => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (isOpen && matches[highlightIndex]) {
        e.preventDefault();
        selectPerson(matches[highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="mb-6">
      <div ref={containerRef} className="relative">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3">
          <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
            Search by Person
          </label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={selectedPerson ? `Showing ${selectedPerson.name} — search another person…` : 'Search a person to see their Objectives → Team KRs → Individual KRs'}
              className="w-full pl-9 pr-20 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              aria-label="Search people"
              aria-expanded={isOpen}
              role="combobox"
              aria-controls="person-okr-search-list"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {(query || selectedPerson) && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                  title="Clear"
                >
                  <X size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={() => { setIsOpen(!isOpen); inputRef.current?.focus(); }}
                className="text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                title="Toggle list"
              >
                <ChevronDown size={16} className={classNames('transition-transform', isOpen && 'rotate-180')} />
              </button>
            </div>
          </div>

          {isOpen && (
            <ul
              id="person-okr-search-list"
              role="listbox"
              className="absolute z-40 left-3 right-3 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-y-auto"
            >
              {matches.length === 0 && (
                <li className="px-3 py-3 text-sm text-gray-500">No people match “{query}”.</li>
              )}
              {matches.map((person, index) => (
                <React.Fragment key={person.id}>
                {/* Sibling <li>, not nested: keyboard nav indexes into `matches`, not
                    DOM children, so this divider is free — but an li inside an li
                    would be invalid markup. */}
                {index > 0 && !person.hasIkrs && !person.isUnassigned && matches[index - 1].hasIkrs && (
                  <li role="presentation" className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50 border-b border-gray-100">
                    Roster · no IKRs assigned
                  </li>
                )}
                <li
                  role="option"
                  aria-selected={person.id === selectedPersonId}
                  onMouseDown={() => selectPerson(person)}
                  onMouseEnter={() => setHighlightIndex(index)}
                  className={classNames(
                    'flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0',
                    index === highlightIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                  )}
                >
                  <span className={classNames(
                    'flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-bold flex-shrink-0',
                    person.isUnassigned ? 'bg-gray-100 text-gray-500' : 'bg-indigo-100 text-indigo-700'
                  )}>
                    {person.isUnassigned ? <User size={14} /> : getPersonInitials(person.name)}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{person.name}</span>
                    <span className="block text-[11px] text-gray-500 truncate">
                      {[person.role, person.email].filter(Boolean).join(' · ') || 'IKRs without an owner'}
                    </span>
                  </span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    {person.hasIkrs ? (
                      <>
                        <span className="text-[11px] text-gray-400">
                          {person.objectives.length} obj · {person.ikrCount} IKR{person.ikrCount !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs font-bold text-blue-600 tabular-nums w-9 text-right">
                          {Math.round(person.progress * 100)}%
                        </span>
                      </>
                    ) : (
                      /* A literal 0% would read as "Behind" rather than "nothing assigned". */
                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        No IKRs
                      </span>
                    )}
                  </span>
                </li>
                </React.Fragment>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selectedPerson && (
        <div className="mt-3 bg-white border border-gray-200 rounded-xl shadow-sm p-4">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className={classNames(
                'flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold flex-shrink-0',
                selectedPerson.isUnassigned ? 'bg-gray-100 text-gray-500' : 'bg-indigo-100 text-indigo-700'
              )}>
                {selectedPerson.isUnassigned ? <User size={18} /> : getPersonInitials(selectedPerson.name)}
              </span>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900 truncate">{selectedPerson.name}</h3>
                <p className="text-xs text-gray-500 truncate">
                  {[selectedPerson.role, selectedPerson.email].filter(Boolean).join(' · ') || 'Individual KRs with no owner assigned'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                <p className="text-[10px] font-bold text-gray-500 uppercase">Objectives</p>
                <p className="text-lg font-bold text-gray-900">{selectedPerson.objectives.length}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-gray-500 uppercase">IKRs</p>
                <p className="text-lg font-bold text-gray-900">{selectedPerson.ikrCount}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-gray-500 uppercase">Progress</p>
                <p className="text-lg font-bold text-blue-600">{Math.round(selectedPerson.progress * 100)}%</p>
              </div>
            </div>
          </div>

          {notice && (
            <div className={classNames(
              'flex items-start justify-between gap-3 mb-3 px-3 py-2 rounded-lg text-xs border',
              notice.tone === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-700'
            )}>
              <span>{notice.text}</span>
              <button type="button" onClick={() => setNotice(null)} className="flex-shrink-0 opacity-60 hover:opacity-100">
                <X size={13} />
              </button>
            </div>
          )}

          {selectedPerson.objectives.length === 0 ? (
            <div className="text-center py-8">
              <Target size={32} className="mx-auto text-gray-300" />
              <p className="text-sm text-gray-500 mt-2">
                {selectedPerson.isUnassigned
                  ? 'No unassigned individual key results.'
                  : 'No Individual KR has been assigned to this person.'}
              </p>
              {/* For a non-admin an empty panel is ambiguous — the person may own IKRs
                  inside objectives outside this viewer's access. */}
              <p className="text-[11px] text-gray-400 mt-1">Only objectives you can access are searched.</p>
            </div>
          ) : (
            // Key by person too, so switching people resets the open/closed state.
            selectedPerson.objectives.map(obj => (
              <ObjectiveBlock key={`${selectedPerson.id}:${obj.id}`} objective={obj} editProps={editProps} />
            ))
          )}
        </div>
      )}

      {!selectedPerson && !people.some(p => p.hasIkrs) && (
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 px-1">
          <Users size={14} className="text-gray-400" />
          No individual key results have been assigned yet.
        </div>
      )}
    </div>
  );
}
